import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';

import { cn, useResponsiveBreakPoints } from '@restworld/utils-common';
import { InformationCircleIcon, ShareIcon } from '@heroicons/react/24/outline';
import { Heading } from '@restworld/ui-ds-base';

// import { useRecordJobShareEvent } from '../../../../hooks/use-record-job-share-event';
import { JobApplicationEntryPoint } from '../../job-application-entry-point';
import { useJobPositionId } from '../../../../hooks';
import { useAtomValue } from 'jotai';
import { jobPositionApiDataAtom } from '../../job-position.atoms';
import { Wrapper } from '@restworld/ui-components';
import { Timeline } from '../../../legacy/timeline';
import { JobShareModal } from '../../../legacy/job-share-modal';

/* eslint-disable-next-line */
export interface JobApplicationStepsProps {
  ambassadorRefCode?: string;
}

export function JobApplicationSteps({ ambassadorRefCode }: JobApplicationStepsProps) {
  const { isDesktop } = useResponsiveBreakPoints();
  const jobPositionId = useJobPositionId();
  const { t: jobPositionTranslate } = useTranslation('job-position-page');
  const jpData = useAtomValue(jobPositionApiDataAtom);

  const timeLineSteps = useMemo(() => {
    const steps = [
      {
        key: 1,
        title: jobPositionTranslate('job_timeline.timeline_step_1.title'),
        subtitle: jobPositionTranslate('job_timeline.timeline_step_1.description'),
      },
      {
        key: 2,
        title: jobPositionTranslate('job_timeline.timeline_step_2.title'),
        subtitle: jobPositionTranslate('job_timeline.timeline_step_2.description'),
      },
      {
        key: 3,
        title: jobPositionTranslate('job_timeline.timeline_step_3.title'),
        subtitle: jobPositionTranslate('job_timeline.timeline_step_3.description'),
      },
      {
        key: 4,
        title: jobPositionTranslate('job_timeline.timeline_step_4.title'),
        subtitle: jobPositionTranslate('job_timeline.timeline_step_4.description'),
      },
      {
        key: 5,
        title: jobPositionTranslate('job_timeline.timeline_step_5.title'),
        // A Partita IVA position ends in a freelance collaboration, not an employment contract.
        subtitle: jobPositionTranslate(
          jpData?.contract_type_var?.key === 'self_employed'
            ? 'job_timeline.timeline_step_5.description_self_employed'
            : 'job_timeline.timeline_step_5.description'
        ),
        isFlagged: true,
      },
    ];
    if (jpData?.published_from_ats) {
      return steps.filter((s) => s.key !== 1).map((s) => ({ ...s, key: s.key - 1 }));
    }
    return steps;
  }, [jobPositionTranslate, jpData?.published_from_ats, jpData?.contract_type_var?.key]);

  return (
    <Wrapper>
      <span className="flex flex-row items-center gap-1.5">
        <InformationCircleIcon className="h-6" />
        <Heading>{jobPositionTranslate('job_timeline.title')}</Heading>
      </span>
      <Timeline timeLineSteps={timeLineSteps} />
      {!isDesktop && (
        <ButtonWrapper>
          <JobShareModal
            jobPositionId={jobPositionId}
            ambassadorRefCode={ambassadorRefCode}
            StartIcon={ShareIcon}
            buttonTitle="Condividi"
            fullwidth
            variant="tertiary"
          />
          <JobApplicationEntryPoint />
        </ButtonWrapper>
      )}
    </Wrapper>
  );
}
export default JobApplicationSteps;
const ButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex gap-4 text-lg justify-center items-center lg:w-full lg:max-w-[21rem] lg:flex-row-reverse [&>button:last-child]:lg:w-4/5',
      className
    )}
    {...props}
  />
);
